import styled from '@emotion/styled';
import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';

import { css } from '~/css';
import usePlatform from '~/shared/hooks/platform';

import { TransitionEventCallback } from './types';

type CSSWidth = string | number;

interface RepeatContainerProps {
  className?: string;
  currentIndex: number;
  // IE11 버그 대응을 위해 정확한 요소 사이즈를 지정해줘야 합니다.
  panelSize: CSSWidth | CSSWidth[];
  onTransitionEnd?: TransitionEventCallback;
  children: React.ReactNodeArray;
}

const RepeatContainer = forwardRef<HTMLDivElement, RepeatContainerProps>(function RepeatContainer(
  { className, currentIndex, panelSize, onTransitionEnd, children },
  ref,
) {
  const totalCount = children.length;
  const slideSize = Array.isArray(panelSize)
    ? panelSize.map((s) => `calc(${s} * ${totalCount})`)
    : `calc(${panelSize} * ${totalCount})`;

  const centerRef = useRef<HTMLDivElement>(null);
  const firstRepeatBoxRef = useRef<HTMLDivElement>(null);
  const turnOffAnimation = useCallback(() => {
    if (!centerRef.current) return;
    centerRef.current.classList.add('animation-off');
    // eslint-disable-next-line
    centerRef.current.offsetTop; // fire reflow
  }, []);
  const turnOnAnimation = useCallback(() => {
    if (!centerRef.current) return;
    centerRef.current.classList.remove('animation-off');
  }, []);
  const { index, onIdleCallback } = useRepeatedIndex(currentIndex, totalCount, turnOffAnimation, turnOnAnimation);

  const handleTransitionEnd: React.TransitionEventHandler = useCallback(
    (e) => {
      if (e.propertyName !== 'transform') return;
      if (e.target !== firstRepeatBoxRef.current) return;
      if (onTransitionEnd) {
        onTransitionEnd();
      }
      onIdleCallback();
    },
    [onIdleCallback, onTransitionEnd],
  );

  const platform = usePlatform();
  const one = 1 / totalCount;
  const half = one / 2;
  const offset = -(index / totalCount) + 0.5;
  const boxStyle: React.CSSProperties = {
    transform: `translateX(${((offset - half) * 100).toFixed(2)}%)`,
    ...(platform === 'ie' && {
      transform: `translateX(${((offset - half) * 100 - 100).toFixed(2)}%)`,
      justifyContent: 'flex-start',
    }),
  };
  return (
    <Container className={className} ref={ref}>
      <CenterBox ref={centerRef} slideSize={slideSize} onTransitionEnd={handleTransitionEnd}>
        <RepeatBox ref={firstRepeatBoxRef} style={boxStyle} slideSize={slideSize}>
          {children}
        </RepeatBox>
        <RepeatBox style={boxStyle} slideSize={slideSize}>
          {children}
        </RepeatBox>
        <RepeatBox style={boxStyle} slideSize={slideSize}>
          {children}
        </RepeatBox>
      </CenterBox>
    </Container>
  );
});
export default RepeatContainer;

const RepeatBox = styled('div')((props: { slideSize: CSSWidth | CSSWidth[] }) =>
  css({
    display: 'flex',
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: props.slideSize,
    width: props.slideSize,
    alignItems: 'center',
    justifyContent: 'space-around',
    flexWrap: 'nowrap',
  })(props),
);

const CenterBox = styled(RepeatBox)(
  css({
    position: 'relative',
    left: '50%',
    transform: 'translateX(-50%)',
    '> *': {
      transition: 'transform 0.5s',
    },
    '&.animation-off > *': {
      transition: 'none',
    },
  }),
);

const Container = styled('div')(
  css({
    position: 'relative',
    overflow: 'hidden',
  }),
);

function useRepeatedIndex(
  currentIndex: number,
  totalCount: number,
  turnOffAnimation: () => void,
  turnOnAnimation: () => void,
) {
  const [rightNow, setRightNow] = useState(false);
  const [index, setIndex] = useState(currentIndex);
  const onIdleCallback = useCallback(() => {
    const repeatedIndex = repeat(index, totalCount);
    if (repeatedIndex !== index) {
      turnOffAnimation();
      setRightNow(true);
      setIndex(repeatedIndex);
    }
  }, [index, totalCount, turnOffAnimation]);
  useEffect(() => {
    if (!rightNow) return;
    turnOnAnimation();
    setRightNow(false);
  }, [turnOnAnimation, rightNow]);
  useEffect(() => {
    const nearestTargetIndex = getNearestTargetIndex(index, currentIndex, totalCount);
    setIndex(nearestTargetIndex);
  }, [currentIndex, index, totalCount]);
  return { index, onIdleCallback };
}

function getNearestTargetIndex(fromIndex: number, toIndex: number, totalCount: number) {
  const repeatedFrom = repeat(fromIndex, totalCount);
  const repeatedTo = repeat(toIndex, totalCount);
  if (repeatedFrom === repeatedTo) return fromIndex;
  const diff = repeatedTo - repeatedFrom;
  const right = diff > 0 ? fromIndex + diff : fromIndex + totalCount + diff;
  const left = right - totalCount;
  return Math.abs(left - fromIndex) < Math.abs(right - fromIndex) ? left : right;
}

function repeat(value: number, max: number) {
  if (value >= 0) return value % max;
  return (value % max) + max;
}
